import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

interface User {
  id: string
  name: string
  email: string
}

interface AppState {
  // User state
  user: User | null
  isAuthenticated: boolean

  // UI state
  isLoading: boolean
  theme: 'light' | 'dark'
  sidebarOpen: boolean

  // Error state
  error: string | null
}

interface AppActions {
  // User actions
  setUser: (user: User | null) => void
  login: (user: User) => void
  logout: () => void

  // UI actions
  setLoading: (loading: boolean) => void
  setTheme: (theme: 'light' | 'dark') => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void

  // Error actions
  setError: (error: string | null) => void
  clearError: () => void

  // Reset
  reset: () => void
}

export type AppStore = AppState & AppActions

const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  theme: 'light',
  sidebarOpen: false,
  error: null,
}

export const useAppStore = create<AppStore>()(
  devtools(
    immer(set => ({
      ...initialState,

      // User actions
      setUser: user =>
        set(state => {
          state.user = user
          state.isAuthenticated = !!user
        }),

      login: user =>
        set(state => {
          state.user = user
          state.isAuthenticated = true
          state.error = null
        }),

      logout: () =>
        set(state => {
          state.user = null
          state.isAuthenticated = false
          state.error = null
        }),

      // UI actions
      setLoading: loading =>
        set(state => {
          state.isLoading = loading
        }),

      setTheme: theme =>
        set(state => {
          state.theme = theme
        }),

      toggleSidebar: () =>
        set(state => {
          state.sidebarOpen = !state.sidebarOpen
        }),

      setSidebarOpen: open =>
        set(state => {
          state.sidebarOpen = open
        }),

      // Error actions
      setError: error =>
        set(state => {
          state.error = error
        }),

      clearError: () =>
        set(state => {
          state.error = null
        }),

      // Reset
      reset: () => set(() => initialState),
    })),
    {
      name: 'app-store',
    }
  )
)
